import { Check, Clock3, Info, Moon, SunMedium, Users } from 'lucide-react';
import { type ReactNode, useState } from 'react';

type NullableScaleValue = 0 | 1 | 2 | 3 | null;
type DisturbanceScaleKey =
  | 'cannotSleepWithin30'
  | 'wakeDuringNight'
  | 'bathroom'
  | 'breathingDiscomfort'
  | 'coughOrSnore'
  | 'cold'
  | 'heat'
  | 'badDreams'
  | 'pain'
  | 'otherReasonFrequency';
type PartnerObservationScaleKey =
  | 'snoreLoud'
  | 'breathingPauses'
  | 'legJerks'
  | 'confusionEpisodes'
  | 'otherRestlessnessFrequency';

export type PSQIScaleValue = 0 | 1 | 2 | 3;
export type PSQIPartnerStatus = 'none' | 'other_room' | 'same_room_other_bed' | 'same_bed';

export interface PSQIDisturbanceAnswers {
  cannotSleepWithin30: PSQIScaleValue;
  wakeDuringNight: PSQIScaleValue;
  bathroom: PSQIScaleValue;
  breathingDiscomfort: PSQIScaleValue;
  coughOrSnore: PSQIScaleValue;
  cold: PSQIScaleValue;
  heat: PSQIScaleValue;
  badDreams: PSQIScaleValue;
  pain: PSQIScaleValue;
  otherReasonFrequency: PSQIScaleValue;
  otherReasonText: string;
}

export interface PSQIPartnerObservationAnswers {
  snoreLoud: PSQIScaleValue | null;
  breathingPauses: PSQIScaleValue | null;
  legJerks: PSQIScaleValue | null;
  confusionEpisodes: PSQIScaleValue | null;
  otherRestlessnessFrequency: PSQIScaleValue | null;
  otherRestlessnessText: string;
}

export interface PSQIComponentScores {
  subjectiveQuality: PSQIScaleValue;
  sleepLatency: PSQIScaleValue;
  sleepDuration: PSQIScaleValue;
  habitualSleepEfficiency: PSQIScaleValue;
  sleepDisturbances: PSQIScaleValue;
  sleepMedicationUse: PSQIScaleValue;
  daytimeDysfunction: PSQIScaleValue;
}

export interface PSQIResultPayload {
  bedtime: string;
  sleepLatencyMinutes: number;
  wakeTime: string;
  sleepDurationHours: number;
  subjectiveQuality: PSQIScaleValue;
  disturbances: PSQIDisturbanceAnswers;
  sleepMedicationUse: PSQIScaleValue;
  daytimeSleepiness: PSQIScaleValue;
  daytimeEnthusiasmProblem: PSQIScaleValue;
  partnerStatus: PSQIPartnerStatus;
  partnerObservations: PSQIPartnerObservationAnswers;
  components: PSQIComponentScores;
  timeInBedHours: number;
  sleepEfficiencyPercent: number;
  total: number;
  classification: string;
  finishedAt?: string;
}

interface PSQITestProps {
  initial?: PSQIResultPayload;
  onCancel?: () => void;
  onNext: (result: PSQIResultPayload) => void;
}

interface PSQIFormState {
  bedtime: string;
  sleepLatencyMinutes: string;
  wakeTime: string;
  sleepDurationHours: string;
  subjectiveQuality: NullableScaleValue;
  disturbances: Record<DisturbanceScaleKey, NullableScaleValue> & { otherReasonText: string };
  sleepMedicationUse: NullableScaleValue;
  daytimeSleepiness: NullableScaleValue;
  daytimeEnthusiasmProblem: NullableScaleValue;
  partnerStatus: PSQIPartnerStatus | '';
  partnerObservations: Record<PartnerObservationScaleKey, NullableScaleValue> & { otherRestlessnessText: string };
}

export const PSQI_FREQUENCY_OPTIONS: ReadonlyArray<{ value: PSQIScaleValue; label: string }> = [
  { value: 0, label: 'Nenhuma no último mês' },
  { value: 1, label: 'Menos de 1 vez por semana' },
  { value: 2, label: '1 a 2 vezes por semana' },
  { value: 3, label: '3 ou mais vezes por semana' },
];

export const PSQI_SUBJECTIVE_QUALITY_OPTIONS: ReadonlyArray<{ value: PSQIScaleValue; label: string }> = [
  { value: 0, label: 'Muito boa' },
  { value: 1, label: 'Boa' },
  { value: 2, label: 'Ruim' },
  { value: 3, label: 'Muito ruim' },
];

export const PSQI_DAYTIME_PROBLEM_OPTIONS: ReadonlyArray<{ value: PSQIScaleValue; label: string }> = [
  { value: 0, label: 'Nenhuma dificuldade' },
  { value: 1, label: 'Um problema leve' },
  { value: 2, label: 'Um problema razoável' },
  { value: 3, label: 'Um grande problema' },
];

export const PSQI_PARTNER_STATUS_OPTIONS: ReadonlyArray<{ value: PSQIPartnerStatus; label: string }> = [
  { value: 'none', label: 'Não' },
  { value: 'other_room', label: 'Parceiro/colega em outro quarto' },
  { value: 'same_room_other_bed', label: 'Mesmo quarto, outra cama' },
  { value: 'same_bed', label: 'Mesmo leito' },
];

export const PSQI_DISTURBANCE_ITEMS = [
  { key: 'cannotSleepWithin30', label: 'Não conseguiu adormecer em até 30 minutos', scoreBucket: 'latency' },
  { key: 'wakeDuringNight', label: 'Acordou no meio da noite ou de manhã cedo', scoreBucket: 'disturbances' },
  { key: 'bathroom', label: 'Precisou levantar para ir ao banheiro', scoreBucket: 'disturbances' },
  { key: 'breathingDiscomfort', label: 'Não conseguiu respirar confortavelmente', scoreBucket: 'disturbances' },
  { key: 'coughOrSnore', label: 'Tossiu ou roncou forte', scoreBucket: 'disturbances' },
  { key: 'cold', label: 'Sentiu muito frio', scoreBucket: 'disturbances' },
  { key: 'heat', label: 'Sentiu muito calor', scoreBucket: 'disturbances' },
  { key: 'badDreams', label: 'Teve sonhos ruins', scoreBucket: 'disturbances' },
  { key: 'pain', label: 'Teve dor', scoreBucket: 'disturbances' },
  { key: 'otherReasonFrequency', label: 'Teve outra razão para dormir mal', scoreBucket: 'disturbances' },
] as const satisfies ReadonlyArray<{
  key: DisturbanceScaleKey;
  label: string;
  scoreBucket: 'latency' | 'disturbances';
}>;

export const PSQI_PARTNER_OBSERVATION_ITEMS = [
  { key: 'snoreLoud', label: 'Ronco forte' },
  { key: 'breathingPauses', label: 'Longas paradas de respiração' },
  { key: 'legJerks', label: 'Contrações ou puxões de pernas' },
  { key: 'confusionEpisodes', label: 'Episódios de desorientação ou confusão' },
  { key: 'otherRestlessnessFrequency', label: 'Outras alterações ou inquietações' },
] as const satisfies ReadonlyArray<{
  key: PartnerObservationScaleKey;
  label: string;
}>;

export const PSQI_COMPONENT_METADATA = [
  {
    key: 'subjectiveQuality',
    label: 'Qualidade subjetiva do sono',
    description: 'Percepção geral do sono no último mês.',
  },
  {
    key: 'sleepLatency',
    label: 'Latência do sono',
    description: 'Tempo para adormecer e frequência para dormir após 30 minutos.',
  },
  {
    key: 'sleepDuration',
    label: 'Duração do sono',
    description: 'Horas efetivas dormidas por noite.',
  },
  {
    key: 'habitualSleepEfficiency',
    label: 'Eficiência habitual do sono',
    description: 'Proporção entre sono dormido e tempo total na cama.',
  },
  {
    key: 'sleepDisturbances',
    label: 'Distúrbios do sono',
    description: 'Despertares, desconfortos e outras perturbações noturnas.',
  },
  {
    key: 'sleepMedicationUse',
    label: 'Uso de medicação para dormir',
    description: 'Frequência do uso de medicamento para ajudar a dormir.',
  },
  {
    key: 'daytimeDysfunction',
    label: 'Disfunção diurna',
    description: 'Sonolência diurna e perda de entusiasmo nas atividades.',
  },
] as const satisfies ReadonlyArray<{
  key: keyof PSQIComponentScores;
  label: string;
  description: string;
}>;

const DEFAULT_FORM_STATE: PSQIFormState = {
  bedtime: '',
  sleepLatencyMinutes: '',
  wakeTime: '',
  sleepDurationHours: '',
  subjectiveQuality: null,
  disturbances: {
    cannotSleepWithin30: null,
    wakeDuringNight: null,
    bathroom: null,
    breathingDiscomfort: null,
    coughOrSnore: null,
    cold: null,
    heat: null,
    badDreams: null,
    pain: null,
    otherReasonFrequency: null,
    otherReasonText: '',
  },
  sleepMedicationUse: null,
  daytimeSleepiness: null,
  daytimeEnthusiasmProblem: null,
  partnerStatus: '',
  partnerObservations: {
    snoreLoud: null,
    breathingPauses: null,
    legJerks: null,
    confusionEpisodes: null,
    otherRestlessnessFrequency: null,
    otherRestlessnessText: '',
  },
};

const isScaleValue = (value: unknown): value is PSQIScaleValue =>
  value === 0 || value === 1 || value === 2 || value === 3;

const coerceScaleValue = (value: unknown): PSQIScaleValue | null => {
  const parsed = typeof value === 'number' ? value : Number.parseFloat(String(value ?? ''));
  return isScaleValue(parsed) ? parsed : null;
};

const parseDecimal = (value: string): number | null => {
  if (!value.trim()) return null;
  const parsed = Number.parseFloat(value.replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
};

const isValidTimeValue = (value: string): boolean => /^\d{2}:\d{2}$/.test(value);

const clampHours = (value: number): number => Number(Math.max(0, Math.min(24, value)).toFixed(2));

const scoreLatencyMinutes = (minutes: number): PSQIScaleValue => {
  if (minutes <= 15) return 0;
  if (minutes <= 30) return 1;
  if (minutes <= 60) return 2;
  return 3;
};

const scoreSleepDurationHours = (hours: number): PSQIScaleValue => {
  if (hours > 7) return 0;
  if (hours >= 6) return 1;
  if (hours >= 5) return 2;
  return 3;
};

const scoreSleepEfficiency = (percent: number): PSQIScaleValue => {
  if (percent >= 85) return 0;
  if (percent >= 75) return 1;
  if (percent >= 65) return 2;
  return 3;
};

const scoreSummedBand = (sum: number): PSQIScaleValue => {
  if (sum <= 0) return 0;
  if (sum <= 2) return 1;
  if (sum <= 4) return 2;
  return 3;
};

const scoreDisturbanceBand = (sum: number): PSQIScaleValue => {
  if (sum <= 0) return 0;
  if (sum <= 9) return 1;
  if (sum <= 18) return 2;
  return 3;
};

const formatHourValue = (hours: number | null): string => {
  if (hours == null || Number.isNaN(hours)) return '—';
  return `${hours.toFixed(1).replace('.', ',')} h`;
};

const formatPercentValue = (percent: number | null): string => {
  if (percent == null || Number.isNaN(percent)) return '—';
  return `${percent.toFixed(1).replace('.', ',')}%`;
};

export const calculateTimeInBedHours = (bedtime: string, wakeTime: string): number | null => {
  if (!isValidTimeValue(bedtime) || !isValidTimeValue(wakeTime)) return null;

  const [bedHour, bedMinute] = bedtime.split(':').map(Number);
  const [wakeHour, wakeMinute] = wakeTime.split(':').map(Number);
  if ([bedHour, bedMinute, wakeHour, wakeMinute].some((value) => !Number.isFinite(value))) return null;

  let bedtimeMinutes = bedHour * 60 + bedMinute;
  let wakeTimeMinutes = wakeHour * 60 + wakeMinute;
  if (wakeTimeMinutes <= bedtimeMinutes) {
    wakeTimeMinutes += 24 * 60;
  }

  const hours = (wakeTimeMinutes - bedtimeMinutes) / 60;
  return hours > 0 ? Number(hours.toFixed(2)) : null;
};

export const getPSQIClassification = (total?: number | null): string => {
  if (typeof total !== 'number') return '—';
  return total <= 5 ? 'Boa qualidade do sono' : 'Má qualidade do sono';
};

const buildPSQIComputedResult = (input: {
  bedtime: string;
  sleepLatencyMinutes: number;
  wakeTime: string;
  sleepDurationHours: number;
  subjectiveQuality: PSQIScaleValue;
  disturbances: PSQIDisturbanceAnswers;
  sleepMedicationUse: PSQIScaleValue;
  daytimeSleepiness: PSQIScaleValue;
  daytimeEnthusiasmProblem: PSQIScaleValue;
  partnerStatus: PSQIPartnerStatus;
  partnerObservations: PSQIPartnerObservationAnswers;
}): PSQIResultPayload | null => {
  const timeInBedHours = calculateTimeInBedHours(input.bedtime, input.wakeTime);
  if (timeInBedHours == null || timeInBedHours <= 0) return null;

  const sleepEfficiencyPercent = Number(((input.sleepDurationHours / timeInBedHours) * 100).toFixed(1));
  const latencyScore = scoreSummedBand(scoreLatencyMinutes(input.sleepLatencyMinutes) + input.disturbances.cannotSleepWithin30);
  const disturbanceSum = [
    input.disturbances.wakeDuringNight,
    input.disturbances.bathroom,
    input.disturbances.breathingDiscomfort,
    input.disturbances.coughOrSnore,
    input.disturbances.cold,
    input.disturbances.heat,
    input.disturbances.badDreams,
    input.disturbances.pain,
    input.disturbances.otherReasonFrequency,
  ].reduce((sum, value) => sum + value, 0);

  const components: PSQIComponentScores = {
    subjectiveQuality: input.subjectiveQuality,
    sleepLatency: latencyScore,
    sleepDuration: scoreSleepDurationHours(input.sleepDurationHours),
    habitualSleepEfficiency: scoreSleepEfficiency(sleepEfficiencyPercent),
    sleepDisturbances: scoreDisturbanceBand(disturbanceSum),
    sleepMedicationUse: input.sleepMedicationUse,
    daytimeDysfunction: scoreSummedBand(input.daytimeSleepiness + input.daytimeEnthusiasmProblem),
  };

  const total = Object.values(components).reduce((sum, value) => sum + value, 0);

  return {
    bedtime: input.bedtime,
    sleepLatencyMinutes: Math.max(0, Math.round(input.sleepLatencyMinutes)),
    wakeTime: input.wakeTime,
    sleepDurationHours: clampHours(input.sleepDurationHours),
    subjectiveQuality: input.subjectiveQuality,
    disturbances: input.disturbances,
    sleepMedicationUse: input.sleepMedicationUse,
    daytimeSleepiness: input.daytimeSleepiness,
    daytimeEnthusiasmProblem: input.daytimeEnthusiasmProblem,
    partnerStatus: input.partnerStatus,
    partnerObservations: input.partnerObservations,
    components,
    timeInBedHours,
    sleepEfficiencyPercent,
    total,
    classification: getPSQIClassification(total),
  };
};

const buildPSQIResultFromForm = (state: PSQIFormState): PSQIResultPayload | null => {
  const sleepLatencyMinutes = parseDecimal(state.sleepLatencyMinutes);
  const sleepDurationHours = parseDecimal(state.sleepDurationHours);

  if (
    !isValidTimeValue(state.bedtime) ||
    !isValidTimeValue(state.wakeTime) ||
    sleepLatencyMinutes == null ||
    sleepLatencyMinutes < 0 ||
    sleepDurationHours == null ||
    sleepDurationHours < 0 ||
    state.subjectiveQuality == null ||
    state.sleepMedicationUse == null ||
    state.daytimeSleepiness == null ||
    state.daytimeEnthusiasmProblem == null ||
    !state.partnerStatus ||
    PSQI_DISTURBANCE_ITEMS.some((item) => state.disturbances[item.key] == null)
  ) {
    return null;
  }

  const disturbances: PSQIDisturbanceAnswers = {
    cannotSleepWithin30: state.disturbances.cannotSleepWithin30!,
    wakeDuringNight: state.disturbances.wakeDuringNight!,
    bathroom: state.disturbances.bathroom!,
    breathingDiscomfort: state.disturbances.breathingDiscomfort!,
    coughOrSnore: state.disturbances.coughOrSnore!,
    cold: state.disturbances.cold!,
    heat: state.disturbances.heat!,
    badDreams: state.disturbances.badDreams!,
    pain: state.disturbances.pain!,
    otherReasonFrequency: state.disturbances.otherReasonFrequency!,
    otherReasonText: state.disturbances.otherReasonText.trim(),
  };

  const partnerObservations: PSQIPartnerObservationAnswers = {
    snoreLoud: state.partnerObservations.snoreLoud,
    breathingPauses: state.partnerObservations.breathingPauses,
    legJerks: state.partnerObservations.legJerks,
    confusionEpisodes: state.partnerObservations.confusionEpisodes,
    otherRestlessnessFrequency: state.partnerObservations.otherRestlessnessFrequency,
    otherRestlessnessText: state.partnerObservations.otherRestlessnessText.trim(),
  };

  return buildPSQIComputedResult({
    bedtime: state.bedtime,
    sleepLatencyMinutes,
    wakeTime: state.wakeTime,
    sleepDurationHours,
    subjectiveQuality: state.subjectiveQuality,
    disturbances,
    sleepMedicationUse: state.sleepMedicationUse,
    daytimeSleepiness: state.daytimeSleepiness,
    daytimeEnthusiasmProblem: state.daytimeEnthusiasmProblem,
    partnerStatus: state.partnerStatus,
    partnerObservations,
  });
};

export const normalizePSQIPayload = (payload: unknown): PSQIResultPayload | null => {
  if (!payload || typeof payload !== 'object') return null;

  const value = payload as Record<string, unknown>;
  const bedtime = typeof value.bedtime === 'string' ? value.bedtime : '';
  const wakeTime = typeof value.wakeTime === 'string' ? value.wakeTime : '';
  const sleepLatencyMinutes =
    typeof value.sleepLatencyMinutes === 'number'
      ? value.sleepLatencyMinutes
      : Number.parseFloat(String(value.sleepLatencyMinutes ?? ''));
  const sleepDurationHours =
    typeof value.sleepDurationHours === 'number'
      ? value.sleepDurationHours
      : Number.parseFloat(String(value.sleepDurationHours ?? ''));
  const subjectiveQuality = coerceScaleValue(value.subjectiveQuality);
  const sleepMedicationUse = coerceScaleValue(value.sleepMedicationUse);
  const daytimeSleepiness = coerceScaleValue(value.daytimeSleepiness);
  const daytimeEnthusiasmProblem = coerceScaleValue(value.daytimeEnthusiasmProblem);
  const partnerStatus =
    value.partnerStatus === 'none' ||
    value.partnerStatus === 'other_room' ||
    value.partnerStatus === 'same_room_other_bed' ||
    value.partnerStatus === 'same_bed'
      ? value.partnerStatus
      : null;
  const disturbancesRaw = value.disturbances && typeof value.disturbances === 'object' ? (value.disturbances as Record<string, unknown>) : {};
  const partnerRaw =
    value.partnerObservations && typeof value.partnerObservations === 'object'
      ? (value.partnerObservations as Record<string, unknown>)
      : {};

  if (
    !isValidTimeValue(bedtime) ||
    !isValidTimeValue(wakeTime) ||
    !Number.isFinite(sleepLatencyMinutes) ||
    sleepLatencyMinutes < 0 ||
    !Number.isFinite(sleepDurationHours) ||
    sleepDurationHours < 0 ||
    subjectiveQuality == null ||
    sleepMedicationUse == null ||
    daytimeSleepiness == null ||
    daytimeEnthusiasmProblem == null ||
    partnerStatus == null
  ) {
    return null;
  }

  const disturbances: PSQIDisturbanceAnswers = {
    cannotSleepWithin30: coerceScaleValue(disturbancesRaw.cannotSleepWithin30) ?? 0,
    wakeDuringNight: coerceScaleValue(disturbancesRaw.wakeDuringNight) ?? 0,
    bathroom: coerceScaleValue(disturbancesRaw.bathroom) ?? 0,
    breathingDiscomfort: coerceScaleValue(disturbancesRaw.breathingDiscomfort) ?? 0,
    coughOrSnore: coerceScaleValue(disturbancesRaw.coughOrSnore) ?? 0,
    cold: coerceScaleValue(disturbancesRaw.cold) ?? 0,
    heat: coerceScaleValue(disturbancesRaw.heat) ?? 0,
    badDreams: coerceScaleValue(disturbancesRaw.badDreams) ?? 0,
    pain: coerceScaleValue(disturbancesRaw.pain) ?? 0,
    otherReasonFrequency: coerceScaleValue(disturbancesRaw.otherReasonFrequency) ?? 0,
    otherReasonText: typeof disturbancesRaw.otherReasonText === 'string' ? disturbancesRaw.otherReasonText : '',
  };

  const normalized = buildPSQIComputedResult({
    bedtime,
    sleepLatencyMinutes,
    wakeTime,
    sleepDurationHours,
    subjectiveQuality,
    disturbances,
    sleepMedicationUse,
    daytimeSleepiness,
    daytimeEnthusiasmProblem,
    partnerStatus,
    partnerObservations: {
      snoreLoud: coerceScaleValue(partnerRaw.snoreLoud),
      breathingPauses: coerceScaleValue(partnerRaw.breathingPauses),
      legJerks: coerceScaleValue(partnerRaw.legJerks),
      confusionEpisodes: coerceScaleValue(partnerRaw.confusionEpisodes),
      otherRestlessnessFrequency: coerceScaleValue(partnerRaw.otherRestlessnessFrequency),
      otherRestlessnessText: typeof partnerRaw.otherRestlessnessText === 'string' ? partnerRaw.otherRestlessnessText : '',
    },
  });

  if (!normalized) return null;

  return {
    ...normalized,
    finishedAt: typeof value.finishedAt === 'string' ? value.finishedAt : undefined,
  };
};

const buildInitialState = (initial?: PSQIResultPayload): PSQIFormState => {
  const normalized = normalizePSQIPayload(initial);
  if (!normalized) return DEFAULT_FORM_STATE;

  return {
    bedtime: normalized.bedtime,
    sleepLatencyMinutes: String(normalized.sleepLatencyMinutes),
    wakeTime: normalized.wakeTime,
    sleepDurationHours: String(normalized.sleepDurationHours),
    subjectiveQuality: normalized.subjectiveQuality,
    disturbances: {
      cannotSleepWithin30: normalized.disturbances.cannotSleepWithin30,
      wakeDuringNight: normalized.disturbances.wakeDuringNight,
      bathroom: normalized.disturbances.bathroom,
      breathingDiscomfort: normalized.disturbances.breathingDiscomfort,
      coughOrSnore: normalized.disturbances.coughOrSnore,
      cold: normalized.disturbances.cold,
      heat: normalized.disturbances.heat,
      badDreams: normalized.disturbances.badDreams,
      pain: normalized.disturbances.pain,
      otherReasonFrequency: normalized.disturbances.otherReasonFrequency,
      otherReasonText: normalized.disturbances.otherReasonText,
    },
    sleepMedicationUse: normalized.sleepMedicationUse,
    daytimeSleepiness: normalized.daytimeSleepiness,
    daytimeEnthusiasmProblem: normalized.daytimeEnthusiasmProblem,
    partnerStatus: normalized.partnerStatus,
    partnerObservations: {
      snoreLoud: normalized.partnerObservations.snoreLoud,
      breathingPauses: normalized.partnerObservations.breathingPauses,
      legJerks: normalized.partnerObservations.legJerks,
      confusionEpisodes: normalized.partnerObservations.confusionEpisodes,
      otherRestlessnessFrequency: normalized.partnerObservations.otherRestlessnessFrequency,
      otherRestlessnessText: normalized.partnerObservations.otherRestlessnessText,
    },
  };
};

const SummaryCard = ({ title, value, helper }: { title: string; value: string; helper: string }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-500">{title}</div>
    <div className="mt-2 text-2xl font-black text-slate-900">{value}</div>
    <div className="mt-1 text-sm text-slate-500">{helper}</div>
  </div>
);

const SectionCard = ({
  icon,
  title,
  helper,
  children,
}: {
  icon: ReactNode;
  title: string;
  helper: string;
  children: ReactNode;
}) => (
  <section className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
    <div className="mb-4 flex items-start gap-3">
      <div className="rounded-2xl bg-slate-100 p-3 text-slate-700">{icon}</div>
      <div>
        <h3 className="text-lg font-bold text-slate-900">{title}</h3>
        <p className="mt-1 text-sm text-slate-500">{helper}</p>
      </div>
    </div>
    <div className="space-y-4">{children}</div>
  </section>
);

const ChoicePills = ({
  name,
  value,
  options,
  onChange,
}: {
  name: string;
  value: NullableScaleValue;
  options: ReadonlyArray<{ value: PSQIScaleValue; label: string }>;
  onChange: (value: PSQIScaleValue) => void;
}) => (
  <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
    {options.map((option) => (
      <label
        key={`${name}-${option.value}`}
        className={`flex cursor-pointer items-center gap-3 rounded-2xl border px-4 py-3 text-sm transition ${
          value === option.value
            ? 'border-sky-600 bg-sky-600 text-white'
            : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
        }`}
      >
        <input
          type="radio"
          className="hidden"
          name={name}
          checked={value === option.value}
          onChange={() => onChange(option.value)}
        />
        <span className="flex h-6 w-6 items-center justify-center rounded-full border border-current text-xs font-bold">
          {option.value}
        </span>
        <span className="flex-1">{option.label}</span>
      </label>
    ))}
  </div>
);

const FrequencySelectRow = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: NullableScaleValue;
  onChange: (value: NullableScaleValue) => void;
}) => (
  <div className="grid gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 md:grid-cols-[minmax(0,1fr)_280px] md:items-center">
    <div className="text-sm font-medium leading-relaxed text-slate-800">{label}</div>
    <select
      className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
      value={value == null ? '' : String(value)}
      onChange={(event) => onChange(event.target.value === '' ? null : (Number(event.target.value) as PSQIScaleValue))}
    >
      <option value="">Selecione</option>
      {PSQI_FREQUENCY_OPTIONS.map((option) => (
        <option key={option.value} value={option.value}>
          {option.value} • {option.label}
        </option>
      ))}
    </select>
  </div>
);

const PartnerStatusPills = ({
  value,
  onChange,
}: {
  value: PSQIPartnerStatus | '';
  onChange: (value: PSQIPartnerStatus) => void;
}) => (
  <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
    {PSQI_PARTNER_STATUS_OPTIONS.map((option) => (
      <label
        key={option.value}
        className={`flex cursor-pointer items-center gap-3 rounded-2xl border px-4 py-3 text-sm transition ${
          value === option.value
            ? 'border-slate-900 bg-slate-900 text-white'
            : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
        }`}
      >
        <input
          type="radio"
          className="hidden"
          name="psqi-partner-status"
          checked={value === option.value}
          onChange={() => onChange(option.value)}
        />
        <span className="flex h-6 w-6 items-center justify-center rounded-full border border-current text-xs font-bold">
          {value === option.value ? '✓' : ''}
        </span>
        <span className="flex-1">{option.label}</span>
      </label>
    ))}
  </div>
);

export default function PSQITest({ initial, onCancel, onNext }: PSQITestProps) {
  const [form, setForm] = useState<PSQIFormState>(() => buildInitialState(initial));

  const computedResult = buildPSQIResultFromForm(form);
  const timeInBedHours = calculateTimeInBedHours(form.bedtime, form.wakeTime);
  const sleepDurationHours = parseDecimal(form.sleepDurationHours);
  const previewEfficiency =
    timeInBedHours != null && sleepDurationHours != null && timeInBedHours > 0
      ? Number(((sleepDurationHours / timeInBedHours) * 100).toFixed(1))
      : null;

  const setDisturbanceValue = (key: DisturbanceScaleKey, value: NullableScaleValue) => {
    setForm((current) => ({
      ...current,
      disturbances: {
        ...current.disturbances,
        [key]: value,
      },
    }));
  };

  const setPartnerObservationValue = (key: PartnerObservationScaleKey, value: NullableScaleValue) => {
    setForm((current) => ({
      ...current,
      partnerObservations: {
        ...current.partnerObservations,
        [key]: value,
      },
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3 rounded-2xl border border-sky-200 bg-sky-50 p-4 text-sky-950">
        <Info className="mt-0.5 h-5 w-5 shrink-0" />
        <div>
          <p className="font-semibold">PSQI — Pittsburgh Sleep Quality Index</p>
          <p className="mt-1 text-sm leading-relaxed text-sky-900/90">
            Considere o <strong>último mês</strong>. O escore global varia de 0 a 21 e valores acima de 5 sugerem pior qualidade do sono.
          </p>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <SummaryCard
          title="Pontuação global"
          value={computedResult ? `${computedResult.total}/21` : '—'}
          helper="Soma dos 7 componentes"
        />
        <SummaryCard
          title="Classificação"
          value={computedResult?.classification ?? 'Preencha'}
          helper="Ponto de corte clássico > 5"
        />
        <SummaryCard
          title="Eficiência"
          value={formatPercentValue(computedResult?.sleepEfficiencyPercent ?? previewEfficiency)}
          helper="Tempo dormido / tempo na cama"
        />
        <SummaryCard
          title="Tempo na cama"
          value={formatHourValue(computedResult?.timeInBedHours ?? timeInBedHours)}
          helper="Calculado entre deitar e levantar"
        />
      </div>

      {computedResult && (
        <div className="rounded-[28px] border border-amber-200 bg-amber-50 p-5">
          <div className="text-[11px] font-black uppercase tracking-[0.22em] text-amber-700">Componentes calculados</div>
          <div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-4">
            {PSQI_COMPONENT_METADATA.map((item) => (
              <div key={item.key} className="rounded-2xl bg-white/90 px-4 py-3">
                <div className="text-xs font-semibold text-slate-500">{item.label}</div>
                <div className="mt-1 text-lg font-black text-slate-900">{computedResult.components[item.key]}/3</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <SectionCard
        icon={<Moon className="h-5 w-5" />}
        title="Horários e duração do sono"
        helper="Esses campos alimentam diretamente os componentes de latência, duração e eficiência habitual."
      >
        <div className="grid gap-4 md:grid-cols-2">
          <label className="space-y-2">
            <span className="text-sm font-medium text-slate-800">1. Horário usual de deitar</span>
            <input
              type="time"
              className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
              value={form.bedtime}
              onChange={(event) => setForm((current) => ({ ...current, bedtime: event.target.value }))}
            />
          </label>

          <label className="space-y-2">
            <span className="text-sm font-medium text-slate-800">2. Minutos que geralmente leva para dormir</span>
            <input
              type="number"
              min="0"
              step="1"
              className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
              value={form.sleepLatencyMinutes}
              onChange={(event) => setForm((current) => ({ ...current, sleepLatencyMinutes: event.target.value }))}
              placeholder="Ex.: 20"
            />
          </label>

          <label className="space-y-2">
            <span className="text-sm font-medium text-slate-800">3. Horário usual de levantar</span>
            <input
              type="time"
              className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
              value={form.wakeTime}
              onChange={(event) => setForm((current) => ({ ...current, wakeTime: event.target.value }))}
            />
          </label>

          <label className="space-y-2">
            <span className="text-sm font-medium text-slate-800">4. Horas de sono por noite</span>
            <input
              type="number"
              min="0"
              step="0.5"
              className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
              value={form.sleepDurationHours}
              onChange={(event) => setForm((current) => ({ ...current, sleepDurationHours: event.target.value }))}
              placeholder="Ex.: 6.5"
            />
          </label>
        </div>
      </SectionCard>

      <SectionCard
        icon={<Clock3 className="h-5 w-5" />}
        title="Frequência dos distúrbios do sono"
        helper="Registre a frequência de cada evento no último mês. Os itens abaixo alimentam os componentes 2 e 5."
      >
        {PSQI_DISTURBANCE_ITEMS.map((item) => (
          <FrequencySelectRow
            key={item.key}
            label={item.label}
            value={form.disturbances[item.key]}
            onChange={(value) => setDisturbanceValue(item.key, value)}
          />
        ))}

        <label className="block space-y-2">
          <span className="text-sm font-medium text-slate-800">Descrição da outra razão para dormir mal</span>
          <textarea
            className="min-h-[96px] w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
            value={form.disturbances.otherReasonText}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                disturbances: {
                  ...current.disturbances,
                  otherReasonText: event.target.value,
                },
              }))
            }
            placeholder="Opcional"
          />
        </label>
      </SectionCard>

      <SectionCard
        icon={<SunMedium className="h-5 w-5" />}
        title="Percepção do sono e impacto durante o dia"
        helper="Essas respostas completam os componentes subjetivos, uso de medicação e disfunção diurna."
      >
        <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <div className="text-sm font-semibold text-slate-900">6. Como você classificaria a qualidade do seu sono de uma maneira geral?</div>
          <ChoicePills
            name="psqi-quality"
            value={form.subjectiveQuality}
            options={PSQI_SUBJECTIVE_QUALITY_OPTIONS}
            onChange={(value) => setForm((current) => ({ ...current, subjectiveQuality: value }))}
          />
        </div>

        <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <div className="text-sm font-semibold text-slate-900">
            7. Com que frequência você tomou medicamento (prescrito ou por conta própria) para ajudar a dormir?
          </div>
          <ChoicePills
            name="psqi-medication"
            value={form.sleepMedicationUse}
            options={PSQI_FREQUENCY_OPTIONS}
            onChange={(value) => setForm((current) => ({ ...current, sleepMedicationUse: value }))}
          />
        </div>

        <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <div className="text-sm font-semibold text-slate-900">
            8. Com que frequência você teve dificuldade para ficar acordado dirigindo, comendo ou em atividades sociais?
          </div>
          <ChoicePills
            name="psqi-daytime-sleepiness"
            value={form.daytimeSleepiness}
            options={PSQI_FREQUENCY_OPTIONS}
            onChange={(value) => setForm((current) => ({ ...current, daytimeSleepiness: value }))}
          />
        </div>

        <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <div className="text-sm font-semibold text-slate-900">
            9. Quão problemático foi manter o entusiasmo para fazer suas atividades habituais?
          </div>
          <ChoicePills
            name="psqi-daytime-enthusiasm"
            value={form.daytimeEnthusiasmProblem}
            options={PSQI_DAYTIME_PROBLEM_OPTIONS}
            onChange={(value) => setForm((current) => ({ ...current, daytimeEnthusiasmProblem: value }))}
          />
        </div>
      </SectionCard>

      <SectionCard
        icon={<Users className="h-5 w-5" />}
        title="Parceiro(a) ou colega de quarto"
        helper="A questão 10 não entra no score global. Use esta seção se houver observador disponível."
      >
        <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <div className="text-sm font-semibold text-slate-900">10. Você tem parceiro(a), esposo(a) ou colega de quarto?</div>
          <PartnerStatusPills
            value={form.partnerStatus}
            onChange={(value) => setForm((current) => ({ ...current, partnerStatus: value }))}
          />
        </div>

        {form.partnerStatus && form.partnerStatus !== 'none' && (
          <>
            {PSQI_PARTNER_OBSERVATION_ITEMS.map((item) => (
              <FrequencySelectRow
                key={item.key}
                label={item.label}
                value={form.partnerObservations[item.key]}
                onChange={(value) => setPartnerObservationValue(item.key, value)}
              />
            ))}

            <label className="block space-y-2">
              <span className="text-sm font-medium text-slate-800">Descrição de outras alterações observadas</span>
              <textarea
                className="min-h-[96px] w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                value={form.partnerObservations.otherRestlessnessText}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    partnerObservations: {
                      ...current.partnerObservations,
                      otherRestlessnessText: event.target.value,
                    },
                  }))
                }
                placeholder="Opcional"
              />
            </label>
          </>
        )}
      </SectionCard>

      <div className="flex flex-col gap-3 rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm md:flex-row md:items-center md:justify-between">
        <div className="text-sm text-slate-600">
          {computedResult ? (
            <>
              PSQI final <span className="font-semibold text-slate-900">{computedResult.total}/21</span>. Valores acima de 5 sugerem pior qualidade do sono.
            </>
          ) : (
            'Preencha todos os campos obrigatórios para liberar o envio do questionário.'
          )}
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            className="rounded-xl bg-slate-100 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-200"
            onClick={onCancel}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="inline-flex items-center rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={!computedResult}
            onClick={() =>
              computedResult &&
              onNext({
                ...computedResult,
                finishedAt: new Date().toISOString(),
              })
            }
          >
            Concluir PSQI
            <Check className="ml-2 h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
